<template>
  <div class="min-h-screen text-white pt-2 md:pt-8">
    <div class="container max-w-screen-lg mx-auto px-4 py-8 h-full">
      <div class="flex items-center gap-4 mb-6">
        <BackToLoginButton v-if="isStandalone" />
        <h1 class="text-2xl md:text-5xl font-bold md:text-center">
          Terms of Service
        </h1>
      </div>
      <div class="flex flex-col gap-4 pt-4 md:pt-8">
        <span>Last Modified July 11, 2024</span>
        <p>
          This Terms of Service agreement (the “Agreement” or “Terms”) apply to
          the access and use of the websites, applications and other products
          and services under Mastermind Cognitive Training, the cloud-hosted
          platform for cognitive training (collectively, our “Services”), which
          are provided by Mastermind Cognitive Training, (“COMPANY,” “us” or
          “we”), an Illinois-based limited liability company, for you
          (“customer”). By clicking to indicate your acceptance of these Terms,
          or otherwise accessing or using the Services, you agree to this
          Agreement.
        </p>

        <h3>A Joint Commitment:</h3>
        <p>
          We want to ensure our customers receive the maximum benefit from
          participation in the Mastermind Cognitive Training program. The
          Mastermind Cognitive Training program is most successful when the
          customer is consistently following the recommended minimum usage of
          the training program.
        </p>

        <h3>Age Requirement:</h3>
        <p>
          We require account holders to be 18 years of age or older and all
          users to be 13 years of age or older. If we determine that a child
          under 18 created a primary account, we will immediately suspend access
          to the account and delete any personal information associated with the
          child.
        </p>

        <h3>Consistency and Completion:</h3>
        <p>
          The Mastermind Cognitive Training program is designed to provide
          customers and/or those associated with their account games and
          activities that are designed to help improve cognitive skills. A
          positive outcome to the program is dependent on customer’s commitment
          to the Company’s recommendations, and is most successful when it is
          completed following the recommended minimum usage, which is completing
          the training program at least three (3) times per week with each
          session completed in its entirety (using both virtual reality and
          tablet platforms). It may be significantly less effective if the games
          and activities are not completed regularly and consistently, or
          portions are skipped. The duration of each program session takes
          approximately 35 minutes to complete.
        </p>

        <h3>License:</h3>
        <p>
          Payment of the membership fee grants customers the right to access the
          Administrative Portal, view the materials, and use the portal to add
          additional users under their account for an additional cost (should
          you choose). The program subscription payment grants customers and/or
          additional users under their account the right to access the training
          program on both the Meta Quest application as well as a tablet
          application (using either an Android or Apple device). The customer
          and/or additional users on their account will continue to have access
          to the training program as long as payment conditions are met (as set
          under “Program Fees and Refunds”). The materials and services
          available to the customer through the administrative portal and
          through the Meta Quest and tablet applications are proprietary to
          Mastermind Cognitive Training. Customers are permitted to use them
          during the license term for their own personal use and/or for any
          individual enrolled under their account. Customers are not permitted
          to use them for any commercial or business purpose or to use them for
          individuals not identified on their account. Customers are permitted
          to print out documents from the administrative portal for personal
          use, but they are not permitted to make copies of our proprietary
          materials or republish our materials anywhere (including social
          media), or otherwise share or use them for any purpose other than
          their use of these materials for a user listed on their account.
          Enrollment in this program provides a customer one (1) user license
          for the administrative program, and this license is non-transferable.
          Login credentials are private and are not to be shared with
          non-enrolled users. Any violation of this license is grounds for the
          Company to terminate a customer’s subscription access as well as
          access to the administrative portal, in addition to any other legal
          actions deemed necessary and appropriate.
        </p>

        <h3>Program Fees and Refunds</h3>
        <h4>Membership Fee:</h4>
        <p>
          Membership fees are exclusive to Facilities, Organizations,
          Businesses, or Affiliate Program partners who sign-up to offer
          Mastermind Cognitive Training as a third-party provider. When these
          customers sign up for our license services, the customer agrees to a
          one-time payment for the membership fee. When the customer completes
          their enrollment, the customer expressly acknowledges and agrees that
          Mastermind Cognitive Training or our third-party payment processor is
          authorized to charge the customer the one-time membership fee.
        </p>

        <h4>Subscriptions:</h4>
        <p>
          When individual customers sign up for our services, the customer
          agrees to a recurring subscription contract for each individual user
          associated with their account with Mastermind Cognitive Training on a
          monthly basis (the “Subscription Term”). When the customer registers
          for a subscription, the customer expressly acknowledges and agrees
          that (A) Mastermind Cognitive Training or our third party payment
          processor is authorized to charge the customer on a monthly basis (in
          addition to any applicable taxes and other charges) for as long as the
          customer’s subscription continues, and (B) the customer’s subscription
          is continuous until the customer cancels it or we suspend or stop
          providing access to the services in accordance with these terms.
        </p>

        <h3>Payment and Billing Information:</h3>
        <p>
          By providing a credit card or other payment method that we accept, the
          customer represents and warrants that Mastermind Cognitive Training is
          authorized to use the designated payment method. In addition, the
          customer authorizes Mastermind Cognitive Training (or our third-party
          payment processor) to charge the designated payment method for the
          total amount of customer’s subscription fees (and applicable taxes and
          other charges). If the payment method cannot be verified, is invalid
          or is otherwise not acceptable, the customer’s account may be
          suspended until the payment is processed. The customer must resolve
          any problem the company encounters in order to proceed. The customer
          acknowledges that the amount billed may vary due to promotional
          offers, changes to their subscription or changes in applicable taxes
          or other charges, and the customer authorizes the company (or our
          third-party payment processor) to charge the provided payment method
          for the corresponding amount.
        </p>
        <p>
          The billing period for a customer starts on the day of the month they
          initially subscribed, and renews on that same date for all subsequent
          months. The billing date is when the payment will be processed. For
          example, if a customer initially subscribed on the 15th of the month,
          that customer will have their payment processed on the 15th of all
          subsequent months. In the event a month does not contain a customer's
          billing date (e.g. the 31st), the customer's payment will be processed
          on the last day of that month. For example, if a customer initially
          subscribed on January 31st, their payment will be processed on
          February 28th (29th in a leap year), March 31st, April 30th, and so
          on.
        </p>

        <h3>Cancellation Policy for Mastermind Cognitive Training:</h3>
        <p>
          The customer may cancel their subscription at any time by contacting
          us at support@mastermindsports.com or by cancelling their subscription
          by accessing their account information in their portal. Such
          cancellation notice must be sent by the designated account owner.
          Inactivity does not constitute automatic cancellation, so unless the
          customer cancels the subscription term in accordance with this
          section, the customer shall continue to be charged for subscription to
          the services. All cancellation requests will take effect at the end of
          the their subscription period in which the cancellation request is
          made, as long as the request is made at least five (5) days in advance
          of the end of the current monthly billing cycle. If the request is
          made inside of five (5) days of the end of the current monthly billing
          cycle, the cancellation will occur at the end of the following monthly
          billing cycle. The customer will be responsible for all fees and any
          applicable taxes and other charges rendered up through the
          cancellation date. In the event of cancellation of the subscription
          term, the access to Mastermind Cognitive Training to authorized users
          shall also terminate.
        </p>
        <p>
          In the event of cancellation of the subscription term, please note
          that we may still send you promotional communications about Mastermind
          Cognitive Training and the company’s products, unless you opt out of
          receiving those communications by following the unsubscribe
          instructions provided therein.
        </p>

        <h3>Assumption, Release, and Indemnity:</h3>
        <p>
          Some of the Mastermind Cognitive Training program involves or may
          involve physical activity, which is referred together herein as the
          “activities”. The activities are or may be vigorous and consequently
          involve inherent risks of injury. Customers understand the demands of
          the activities relative to the user’s physical condition and skill
          level. The customer represents that all users listed on their account
          do not have any physical or health problems that would preclude the
          user from participating in the Activities. Although the program was
          developed with safety in mind, it is impossible to eliminate all risk
          and possibility of injury. The customer understands that some of the
          inherent risks and potential injuries include but are not limited to
          slipping, falling, tripping, bruises, muscle strain, sprains, rug or
          exercise mat burns; collisions with other people or objects; broken
          bones; cuts; overexertion; concussions; eye injuries; paralysis; brain
          injury; equipment failure; error of judgment by all staff members or
          third-parties; and physical, emotional, or other types of injury to a
          user. The customer understands and voluntarily assumes all inherent
          risks, potential injuries, and infection from any virus or disease and
          agrees that no amount of care, caution, instruction or expertise can
          eliminate all inherent risks and potential injuries. To the fullest
          extent permitted by law, the customer on behalf of the customer,
          additional users associated with their account, their heirs, executors
          and representatives voluntarily assume all inherent risks and
          potential injuries associated with the activities and the program;
          hold harmless, agree not to sue, agree to indemnify, waive and release
          Mastermind Cognitive Training, its owners, officers, directors,
          shareholders, members, managers, partners, agents, employees,
          contractors, representatives, heirs, assigns, volunteers, parent
          companies, subsidiaries, franchisors, equipment providers, insurers,
          landlords, and their affiliates (collectively, the “Protected
          Parties”) from and against all liabilities, losses including loss of
          consortium, damages, claims, demands, actions, suits, causes of
          action, costs, fees, and expenses (including reasonable attorney’s
          fees) relating to, resulting from, or arising out of, or alleged to
          have arisen out of (in whole or in part) any injury, damage, or loss
          (a) that occurs in connection with the customer’s and user’s
          participation in the Mastermind Cognitive Training program, (b) that
          occurs in or about the center or where any of the activities are
          taking place or in transportation to or from the center, or (c) that
          results from the ordinary negligence of any of the protected parties
          to the maximum extent allowed by the law, the foregoing releases shall
          apply even if an injury is caused and/or such claim or demand is
          based, in whole or in part, upon the legal theory of strict liability.
        </p>

        <h3>Dispute Resolution:</h3>
        <p>
          The customer agrees that any dispute or claim arising out of or
          relating to this Agreement, the program, or the Activities, including
          any personal injury, shall be brought by a party individually and not
          as a plaintiff or class member in any purported class or
          representative capacity and shall be determined by binding arbitration
          before a single arbitrator administered by the American Arbitration
          Association. No award shall exceed the amount of the claim and the
          arbitrator shall have no authority to award punitive or exemplary
          damages. If a party files suit in violation of this paragraph (except
          to toll the statute of limitations), such party shall reimburse the
          other for their costs and expenses, including attorneys’ fees,
          incurred in seeking abatement of such suit and enforcement of this
          paragraph. To the maximum extent permitted by law, the customer and
          all associated users, their heirs, executors and representatives,
          knowingly and voluntarily, with full awareness of the legal
          consequences, after consulting with legal counsel (or after having
          waived the opportunity to consult with counsel) waives the right to a
          bench trial or jury trial of any and all disputes and agrees to
          resolve any and all disputes through arbitration.
        </p>

        <h3>Additional Terms:</h3>
        <p>
          Users who create an account or use the Services agree to the terms of
          the Mastermind Cognitive Training privacy policy, available here.
          Mastermind Cognitive Training reserves the right and shall be free to
          revise its program from time to time in its reasonable discretion and
          business judgment. The terms of this Agreement shall apply to the
          customer’s initial term and all subsequent enrollments and
          participation in the program. This Agreement shall be binding upon and
          inure to the benefit of the parties hereto and to their respective
          heirs, representatives, and successors. Electronic copies of the
          Agreement hereon shall have the same legal status as originals.
          Customers have been and are hereby urged to consult with attorneys and
          advisors concerning the advisability of entering into this Agreement
          if you feel necessary. If any portion of this Agreement is ever
          declared to be invalid or unenforceable, such portion shall be deemed
          severed from this Agreement, however, the remaining parts of the
          Agreement, including the remaining parts of the paragraph containing
          the unenforceable portion, shall remain in full force to the fullest
          extent possible. Customers agree that there are no oral agreements,
          representations, premises, or warranties other than any expressly set
          forth herein and that customers are not relying on any statements or
          representations that are not expressly contained herein. This
          Agreement constitutes the entire and exclusive understanding and
          agreement between the parties and supersedes any previous
          negotiations, agreements, or representations. This Agreement shall be
          construed fairly and equally as to all parties and the parties hereby
          irrevocably waive the benefit of any rule of contract construction
          which disfavors the drafter of an agreement. This Agreement may only
          be amended in a writing signed by all parties. Where customers are the
          parent or legal guardian of a child under the age of 18, customers
          represent and warrant to the Protected Parties that customers have the
          legal capacity and actual authority to act for and on behalf of the
          child and any other parent or guardian of the child and to execute
          this Agreement on their behalf, including but not limited to, the
          arbitration clause, waiver and release of claims and indemnity, and
          customers agree to indemnify and defend the Protected Parties from and
          against all claims or liabilities resulting from or relating to any
          insufficiency of the parent or legal guardian’s legal capacity or
          authority to act for or on behalf of the student.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import BackToLoginButton from "@/components/general/BackToLoginButton.vue";
import { computed } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const isStandalone = computed(() => {
  return route.meta.isStandalone;
});
</script>

<style scoped lang="postcss">
h3 {
  @apply text-2xl font-bold;
}

h4 {
  @apply text-xl font-bold;
}
</style>
